import CampaignPlatformType from '@/Enums/CampaignManagement/CampaignPlatformType.enum';
import DateFormat from '@/Enums/Format/DateFormat.enum';
import {
  partialUpdateCampaign,
  partialUpdateDV360InsertionOrder,
  partialUpdateDV360LineItem,
  partialUpdateLineItem,
} from '@/store/slices/campaignManagementSlice';
import { LocalizationProvider, MobileDateTimePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import PropTypes from 'prop-types';
import { memo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

const EndDateInput = memo(({ defaultValue, campaignId, lineItemId, minDate, platform }) => {
  const dispatch = useDispatch();

  const [date, setDate] = useState(defaultValue);
  const [isLoading, setIsLoading] = useState(false);

  const handleDateAccept = async newDate => {
    if (!newDate) return;
    try {
      setIsLoading(true);
      setDate(newDate);
      const isDV360 = platform === CampaignPlatformType.Google;
      const endDate = moment.utc(newDate).format(isDV360 ? DateFormat.Iso8601 : DateFormat.Iso8601WithTime);
      if (lineItemId !== undefined) {
        const partialUpdateAction = isDV360 ? partialUpdateDV360LineItem : partialUpdateLineItem;
        await dispatch(partialUpdateAction({ campaignId, lineItemId, endDate })).unwrap();
      } else {
        const partialUpdateAction = isDV360 ? partialUpdateDV360InsertionOrder : partialUpdateCampaign;
        await dispatch(partialUpdateAction({ campaignId, endDate })).unwrap();
      }
      toast.success(`${lineItemId !== undefined ? 'Line Item' : 'Campaign'} updated`);
    } catch (error) {
      setDate(defaultValue);
      toast.error(`Failed to update ${lineItemId !== undefined ? 'Line Item' : 'Campaign'}: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const momentDate = date ? moment.utc(date) : null;
  const momentMinDate = minDate ? moment.utc(minDate) : undefined;

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <MobileDateTimePicker
        orientation='landscape'
        value={momentDate && momentDate.isValid() ? momentDate : null}
        format={DateFormat.NumericMonthDayYearTime}
        onAccept={handleDateAccept}
        minDate={momentMinDate && momentMinDate.isValid() ? momentMinDate : undefined}
        disabled={isLoading}
        slotProps={{
          textField: {
            error: false,
            sx: {
              '& .MuiInputBase-root': {
                height: '40px',
                fontWeight: 600,
                fontSize: '14px',
                border: '1px solid #DDE3E0',
                borderRadius: '6px',
                backgroundColor: '#fff',
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
              },
              '& .MuiInputBase-input': {
                padding: '8px 14px',
              },
            },
          },
        }}
      />
    </LocalizationProvider>
  );
});

EndDateInput.propTypes = {
  defaultValue: PropTypes.string,
  campaignId: PropTypes.number,
  lineItemId: PropTypes.number,
  minDate: PropTypes.string,
  platform: PropTypes.string,
};

export default EndDateInput;
